<template>
  <div
    id="SearchPage"
    class="search"
  >
    <div class="search__title p-8">
      <h1
        class="search__title__h1 h1--underline"
      >
        <span
          class="search__title__h1__query"
          v-tv:searchResults="{messageKey: 'Title', messageValues: { searchWord: getSearchTerm }}"
        />
      </h1>
    </div>
    <div class="search__body">
      <div
        v-if="customerSuggestions.length > 0"
        class="suggestion_wrapper"
      >
        <suggestions-grid
          :suggestions="customerSuggestions"
          :enable-pagination="customerSuggestions.length > MAX_SUGGESTIONS_DISPLAY"
          @suggestion-clicked="openBuyBoxSku"
          :max-suggestions="MAX_SUGGESTIONS_DISPLAY"
          @buybox-clicked="openBuyBoxSku"
          :search-term="getSearchTerm"
          :availabilities="suggestionAvailabilities"
          :loaded-availability-skus="loadedSuggestionAvailabilitySkus"
          class="search-suggestions"
        />
        <div
          v-if="count > 0"
          class="suggestion_scroll_button"
        >
          <p v-tv:searchResults="'scrollText'" />
          <ReactiveButton
            :is-primary-button="false"
            @click="scrollToProducts"
          >
            <span v-tv:searchResults="'scrollButton'" />
            <ArrowDownIcon class="scroll-arrow-icon" />
          </ReactiveButton>
        </div>
      </div>

      <hr
        v-if="customerSuggestions.length > 0"
        class="suggestions-divider"
      >
      <div
        class="filter--desktop"
        :class="{'filter--mobile': isMobile && openFilter}"
        id="searchResultsFilterSection"
      >
        <div class="filter-container search__body__filter">
          <CategoryFilter
            v-if="filters && products && products.facets"
            ref="categoryFilterRef"
            :filters="filters"
            :facets="products.facets"
            :product-count="products.count"
            :is-mobile="isMobile"
            :key="componentKey_filter"
            @applyFilters="handleApplyFilter"
            @filterBuyableProducts="loadProducts"
            @categoryChange="catchCategoryChange"
            @filterInchValues="loadProducts"
            :show-reset-on-ccrs="false"
          />
          <FilterSkeleton
            v-else-if="firstLoad"
          />
        </div>
      </div>
      <PopupBase
        :show="openFilterModal"
        content-css-class="filter__popup__content"
        @close-pop-up="openFilterModal = false"
      />
      <div
        class="button-container"
        v-if="isMobile && filters && filters.length > 0"
      >
        <kbutton
          :theme-color="'primary'"
          @click="openFilter = true"
        >
          <span class="subline">
            {{ $tv('filter', 'categories') }}
          </span>
        </kbutton>
        <kbutton
          :disabled="this.filters.filter(f => f.isActive).length <= 0"
          :theme-color="'base'"
          @click="resetFilters"
        >
          <span
            class="subline"
            v-tv:categories="'reset-filter'"
          />
        </kbutton>
      </div>
      <div class="search__body__table">
        <div
          v-if="products && products.count === 0 && !firstLoad && !resetInProgress"
          class="search__no-results"
        >
          <h4>
            {{ $tv({ messageKey: 'results', messageValues: { total: '0' } }, 'categories') }}
          </h4>
          <div class="search__no-results__headline" />
          <div class="search__no-results__content">
            <div
              v-if="hasActiveFilters"
              class="search__no-results__message"
            >
              <ResetFilters class="show_no_products_icon" />
              <div class="search__no-results__description">
                {{ $tv('noProductsFoundReset', 'categories') }}
              </div>
              <div class="search__no-results__button">
                <ReactiveButton
                  :button-text="$tv('reset-filter', 'categories')"
                  :is-primary-button="false"
                  @click="resetFilters"
                />
              </div>
            </div>
            <div
              v-else
              class="search__no-results__message"
            >
              <ResetFilters class="show_no_products_icon" />
              <div class="search__no-results__description">
                <span>
                  {{ $tv({ messageKey: 'noSearchResultsWithTerm', messageValues: { searchTerm: getSearchTerm } }, 'searchResults') }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <ProductTable
          v-show="products && products.count > 0 && !firstLoad"
          :is-loading="productsLoading || categoryFiltersLoading"
          :columns="categoryColumnsTable"
          :products="mappedProducts"
          :sort="tableOptions.sort"
          :pagination="tableOptions.pagination"
          :total="count"
          :is-search-result="true"
          :is-multiple-categories="PrimaryL2Category === null"
          :prices="prices"
          :availabilities="availabilities"
          :successor-availabilities="successorAvailabilities"
          :loading-indicators="loadingIndicators"
          :is-default-customer="isDefaultCustomer"
          :is-any-search="isAnySearch"
          :search-term="getSearchTerm"
          :key="componentKey"
          @favoriteItem="setItemAsFavorite"
          @shopItem="addItemToBasket"
          @sortByColumn="sortItems"
          @changePage="changePage"
          @filterInchValues="loadProducts"
          @clickItem="clickItem"
          @calculate="handleCalculate"
          ref="productTable"
        />
        <ProductTableSkeleton
          v-show="firstLoad"
        />
      </div>
    </div>
    <PopupBase
      :show="openCartModal"
      @close-pop-up="openCartModal = false"
      css-class="popup__buy-box-new"
    >
      <client-only>
        <ProductBuyBox
          v-if="product"
          :is-default-customer="isDefaultCustomer"
          :product="product"
          :product-data="product"
          class="buy-box-in-popup"
          @productSuccessfullyAdded="onProductSuccessfullyAdded"
          @reloadProduct="reloadProduct"
        />
      </client-only>
    </PopupBase>
  </div>
</template>

<script>
import ProductTable from '../../components/product-detail/ProductTable'
import CategoryFilter from '~/components/category/CategoryFilter'
import PopupBase from '~/components/popup/PopupBase'
import ProductBuyBox from '~/components/product-detail/ProductBuyBox.vue'
import ResetFilters from '~/assets/icons/no_products_found.svg?component'
import ReactiveButton from '~/components/ReactiveButton'
import {useProductService} from '@/composables/product_service'
import searchResultFilters from '~/assets/js/filters/search-result-filters'
import {useCategoryFilters} from '~/composables/category_filters'
import {
  sortFilters,
  cleanupFacets,
  cleanupFilters,
  SHOW_INCH_VALUES_FILTER,
  INCH_FILTER
} from '~/assets/js/filters'
import {useTablePriceAvailability} from '~/composables/useTablePriceAvailability'
import {useDefaultCustomer} from '~/composables/default_customer'
import {NON_ATTRIBUTE_FILTERS, NON_ATTRIBUTE_FILTERS_NEW, PROPERTY_SCOPE_NAMES, FACET_VALUE_TYPES} from '~/assets/js/constants'
import {useReloadProduct} from '~/composables/reload_product'
import {getMappedProducts} from '~/assets/js/utils/product_attributes_mapping'
import FilterSkeleton from '~/components/skeletons/FilterSkeleton.vue'
import ProductTableSkeleton from '~/components/skeletons/ProductTableSkeleton.vue'
import {Button} from '@progress/kendo-vue-buttons'
import {useProductDataStore} from '@/store/product-data'
import {useUserStore} from '@/store/user'
import {useCartStore} from '@/store/cart'
import {useConfigurationDataStore} from '@/store/configuration-data'
import {storeToRefs} from 'pinia'
import SuggestionsGrid from '~/components/search/SuggestionsGrid.vue'
import ArrowDownIcon from '~/assets/icons/md_arrow_downward.svg?component'
import {usePimHelper} from '@/composables/pim_helper'
import {usePandaService} from '@/composables/panda_service'
import {useGtm} from '@gtm-support/vue-gtm'
import {useEventItemConverter} from '@/composables/useEventItemConverter'
import {useDynamicEvents} from '~/composables/useDynamicEvents'

export default defineComponent({
  name: 'SearchResults',
  components: {
    kbutton: Button,
    CategoryFilter,
    PopupBase,
    ProductBuyBox,
    ProductTable,
    ResetFilters,
    ReactiveButton,
    FilterSkeleton,
    ProductTableSkeleton,
    SuggestionsGrid,
    ArrowDownIcon
  },
  data () {
    return {
      productsLoading: true,
      count: 0,
      openCartModal: false,
      categoryFilters: [],
      categoryColumnsTable: [],
      categoryFiltersTable: [],
      defaultFilters: [],
      primaryL2Filter: Object.assign({}, {}),
      categoryFiltersLoading: false,
      componentKey: 0,
      componentKey_filter: 0,
      firstLoad: true,
      categoryAttributeSelects: [],
      openFilter: false,
      isAnySearch: false,
      lastUpdatedL2Category: null,
      customerSuggestions: [],
      MAX_SUGGESTIONS_DISPLAY: 8,
      suggestionAvailabilities: [],
      loadedSuggestionAvailabilitySkus: []
    }
  },
  setup() {
    const {addDesignAssets} = useProductDataStore()
    const {showInchValues} = storeToRefs(useUserStore())
    const {deliveryAddressUuid} = storeToRefs(useCartStore())
    const {experimentalMode} = storeToRefs(useConfigurationDataStore())
    const {getCategoryAttributesByCategoryName} = useCategoryDesignService()
    const {getProductsBySearchTermNew, getCustomerSuggestions, getProductBySkuNew} = useProductService()
    const {mapCategoryFilter, getCategoryFiltersFromJson, loadProductCategoryWithPim, mapCategoryTableColumns} = usePimHelper()
    const {postPandaAvailabilitiesBulk} = usePandaService()
    const {isDefaultCustomer} = useDefaultCustomer()
    const route = useRoute()
    const nuxtApp = useNuxtApp()
    const i18n = useI18n()
    const {buildDynamicFilterProps} = useDynamicEvents()

    const product = ref(null)
    const {reloadProduct, reloadProductsInProgress} = useReloadProduct(product, i18n.locale, nuxtApp.$globalization)

    // Use the table price availability composable
    const products = ref([])
    const {
      prices,
      availabilities,
      successorAvailabilities,
      loadingIndicators,
      loadPrices,
      loadAvailabilities,
      loadSuccessorAvailabilities
    } = useTablePriceAvailability({
      products,
      hasRight: (right) => nuxtApp.$auth.loggedIn && useUserStore().rights?.some((item) => item === right),
      isLoggedIn: computed(() => nuxtApp.$auth.loggedIn)
    })

    // Category filters composable - the categoryFilter ref will be set via template ref
    const categoryFilterRef = ref(null)
    // Create a wrapper function that will call the component's loadProducts method
    // This is needed because loadProducts is defined in methods which isn't available during setup
    const loadProductsWrapper = ref(() => {})
    const {
      filters,
      tableOptions,
      isMobile,
      openFilterModal,
      hasActiveFilters,
      resetInProgress,
      filter,
      sortItems,
      changePage,
      setParamsInUrl
    } = useCategoryFilters(() => loadProductsWrapper.value(), categoryFilterRef)

    return {
      route,
      'assets': addDesignAssets,
      showInchValues,
      deliveryAddressUuid,
      experimentalMode,
      getCategoryAttributesByCategoryName,
      getProductsBySearchTermNew,
      getCustomerSuggestions,
      getProductBySkuNew,
      mapCategoryFilter,
      getCategoryFiltersFromJson,
      loadProductCategoryWithPim,
      mapCategoryTableColumns,
      postPandaAvailabilitiesBulk,
      isDefaultCustomer,
      product,
      reloadProduct,
      reloadProductsInProgress,
      // From the composable
      products,
      prices,
      availabilities,
      successorAvailabilities,
      loadingIndicators,
      loadPrices,
      loadAvailabilities,
      loadSuccessorAvailabilities,
      // From category filters composable
      filters,
      tableOptions,
      isMobile,
      openFilterModal,
      hasActiveFilters,
      resetInProgress,
      filter,
      sortItems,
      changePage,
      setParamsInUrl,
      categoryFilterRef,
      loadProductsWrapper,
      buildDynamicFilterProps
    }
  },
  computed: {
    getSearchTerm() {
      if (this.route.query !== null && typeof(window) !== 'undefined') {
        var sanel = window.document.createElement('span')
        sanel.innerText = this.route.query.query
        return sanel.innerHTML
      }
      return ''
    },
    PrimaryL2Category() {
      if (this.route.query !== null && typeof(window) !== 'undefined') {
        if (this.route.query?.PrimaryL2Category) {
          return this.route.query?.PrimaryL2Category
        }
      }
      if (this.products?.facets?.primaryL2Category?.length === 1 &&
          this.products?.count === this.products?.facets?.primaryL2Category[0]?.count) {
        return this.products?.facets?.primaryL2Category[0]?.value
      }
      return null
    },
    mappedProducts() {
      return getMappedProducts(this.products)
    },
    breadcrumbs() {
      return [{label: this.$tv('searchresults', 'breadcrumbs')}]
    }
  },
  mounted() {
    // Set the loadProducts wrapper so the composable can call loadProducts when URL changes
    this.loadProductsWrapper = this.loadProducts
    this.filters = cleanupFilters(searchResultFilters)
    this.adjustFilters(this.PrimaryL2Category)
    this.adjustTableColumns(this.PrimaryL2Category)
    useBreadcrumb(this.breadcrumbs)
  },
  updated() {
    const router = this.$router
    const currentRoute = router.currentRoute.value
    if ((this.lastUpdatedL2Category === null || this.lastUpdatedL2Category !== this.PrimaryL2Category) && currentRoute.name.startsWith('searchResults:')) {
      this.lastUpdatedL2Category = this.PrimaryL2Category
      this.setParamsInUrl(this.lastUpdatedL2Category)
    }
  },
  methods: {
    async loadProducts() {
      if (this.$auth.loggedIn) {
        const customerRes = await this.getCustomerSuggestions(this.getSearchTerm, this.$globalization.getRegion(), this.$i18n.locale)
        this.customerSuggestions = customerRes?.searchResults || []
        this.loadSuggestionAvailabilities()
      } else {
        this.customerSuggestions = []
        this.suggestionAvailabilities = []
        this.loadedSuggestionAvailabilitySkus = []
      }
      const filters = NON_ATTRIBUTE_FILTERS_NEW
      const pricingFlag = 'pricingFlag'
      if (this.PrimaryL2Category !== null) {
        // Only request facets for filters that need dropdown values from backend
        this.facets = this.filters.filter(cat => cat.indexPath !== null
          && cat.indexPath !== ''
          && FACET_VALUE_TYPES.includes(cat.type)
          && cat.indexPath.toLowerCase() !== filters.POLYMER.toLowerCase()
          && cat.indexPath.toLowerCase() !== filters.DESIGN.toLowerCase()
          && cat.indexPath.toLowerCase() !== filters.PRIMARYL2CATEGORY.toLowerCase()
          && cat.indexPath.toLowerCase() !== filters.MATERIALIDENTIFIER.toLowerCase()
          && cat.indexPath.toLowerCase() !== filters.BRAND.toLowerCase()).map(cat => cat.indexPath)
        this.facets = cleanupFacets(this.facets)
        this.facets.push(filters.POLYMER)
        this.facets.push(filters.DESIGN)
        this.facets.push(filters.BRAND)
        this.facets.push(filters.PRIMARYL2CATEGORY)
        this.facets.push(filters.MATERIALIDENTIFIER)
        this.facets.push(pricingFlag)
      } else {
        this.facets = []
        this.facets.push(filters.POLYMER)
        this.facets.push(filters.DESIGN)
        this.facets.push(filters.BRAND)
        this.facets.push(filters.PRIMARYL2CATEGORY)
        this.facets.push(filters.MATERIALIDENTIFIER)
        this.facets.push(pricingFlag)
      }
      const enhanceQueryFilters = [...this.filters]
      let applyCustomFilter = this.showInchValues ? [SHOW_INCH_VALUES_FILTER.customQuery, INCH_FILTER.customQuery] : []

      this.products = await this.getProductsBySearchTermNew(this.getSearchTerm, this.facets, this.$globalization.getRegion(), this.$i18n.locale, this.tableOptions, enhanceQueryFilters, applyCustomFilter, PROPERTY_SCOPE_NAMES.SEARCH_RESULT_PAGE_NEW, this.categoryAttributeSelects)
      this.count = this.products?.count
      this.isAnySearch = this.products?.isAnySearch ?? false

      if (this.$gtm) {
        this.$gtm?.trackEvent({
          event: 'search',
          category: 'product_search',
          action: 'resultpage_search',
          ecommerce: {'search_term': this.getSearchTerm, 'search_filter': JSON.stringify(this.filters.filter(f => f.activeValues).map(f => ({'filter': f.value, 'value': f.activeValues}))), 'search_count': this.count}
        })
      }

      this.$searchInsights?.trackEvent({
        name: 'Search', properties: {
          searchServiceName: this.products?.azureSearchTelemetry?.searchServiceName,
          searchId: this.products?.azureSearchTelemetry?.searchId,
          indexName: this.products?.azureSearchTelemetry?.indexName,
          queryTerms: this.getSearchTerm,
          resultCount: this.products?.count,
          scoringProfile: this.products?.azureSearchTelemetry?.scoringProfile === null ? '' : this.products?.azureSearchTelemetry?.scoringProfile
        }
      })
      this.productsLoading = false
      if (this.firstLoad) {
        this.firstLoad = false
      }
    },
    setItemAsFavorite(e) {
      // no NUXT3/VUE3 TODO favorites
      // eslint-disable-next-line no-console
      console.log('setItemAsFavorite', e)
    },
    addItemToBasket(e, product) {
      this.product = product
      this.openCartModal = true
    },
    onProductSuccessfullyAdded() {
      this.openCartModal = false
    },
    clickItem(sku, rank) {
      this.$searchInsights?.trackEvent({
        name: 'Click', properties: {
          searchServiceName: this.products?.azureSearchTelemetry?.searchServiceName,
          searchId: this.products?.azureSearchTelemetry?.searchId,
          clickedDocId: sku,
          rank: rank
        }
      })
    },
    async adjustTableColumns(category) {
      this.categoryFiltersLoading = true
      const categoryJson = await this.loadProductCategoryWithPim(category, this.$route)
      this.categoryColumnsTable = this.mapCategoryTableColumns(categoryJson)
      this.categoryAttributeSelects = this.categoryColumnsTable.map(f => f.Attribute.IndexPath)
      this.defaultFilters.forEach(filter => {
        this.categoryColumnsTable.push(filter)
      })
      this.categoryFiltersLoading = false
    },
    async catchCategoryChange(event) {
      await this.adjustFilters(event.value)
      this.componentKey_filter += 1
    },
    async adjustFilters(category) {
      if (category !== '' && category !== null) {
        let index = this.filters.findIndex(filter => filter.indexPath === NON_ATTRIBUTE_FILTERS.PRIMARYL2CATEGORY)
        if (index !== -1) {
          this.filters[index].isDisabled = true
        }
        this.categoryFiltersTable = await this.getCategoryFiltersFromJson(category)
        let newCategoryFilters = cleanupFilters(this.categoryFiltersTable)
        newCategoryFilters = newCategoryFilters.filter(ca => ca.indexPath !== null)

        // Use normalized shortName for deduplication to handle case variations
        let newCategoryFilterNames = new Set(newCategoryFilters.map(f => (f.shortName || '').toLowerCase()))
        let categoryFilters = this.filters.filter(f => {
          const normalizedShortName = (f.shortName || '').toLowerCase()
          return newCategoryFilterNames.has(normalizedShortName)
        })
        let existingCategoryFilterNames = new Set(categoryFilters.map(f => (f.shortName || '').toLowerCase()))

        // Only add new filters that don't already exist (by shortName)
        this.categoryFilters = categoryFilters.concat(
          newCategoryFilters.filter(f => {
            const normalizedShortName = (f.shortName || '').toLowerCase()
            return !existingCategoryFilterNames.has(normalizedShortName)
          })
        )

        let currentPrimaryL2Filter = this.filters.find(f =>
          f.shortName.toLowerCase() === NON_ATTRIBUTE_FILTERS.PRIMARYL2CATEGORY.toLowerCase()
        )
        if (currentPrimaryL2Filter) {
          currentPrimaryL2Filter.isDisabled = false
        }
        let currentDefaultFilters = this.getDefaultFilters().filter(df =>
          !this.categoryFilters.some(cf =>
            (cf.shortName || '').toLowerCase() === (df.shortName || '').toLowerCase()
          )
        )

        // Rebuild filters array without duplicates
        this.filters = []
        if (currentPrimaryL2Filter) {
          this.filters.push(currentPrimaryL2Filter)
        }
        this.categoryFilters.forEach(filter => {
          this.filters.push(filter)
        })
        currentDefaultFilters.forEach(filter => {
          this.filters.push(filter)
        })
      } else {
        let currentPrimaryL2Filter = this.filters.find(f =>
          f.shortName.toLowerCase() === NON_ATTRIBUTE_FILTERS.PRIMARYL2CATEGORY.toLowerCase()
        )
        let currentDefaultFilters = this.getDefaultFilters()

        // Rebuild filters array without duplicates
        this.filters = []
        if (currentPrimaryL2Filter) {
          this.filters.push(currentPrimaryL2Filter)
        }
        currentDefaultFilters.forEach(filter => {
          this.filters.push(filter)
        })
      }
      this.filters = sortFilters(this.filters)
    },
    getDefaultFilters() {
      return this.filters.filter(f =>
        f.shortName.toLowerCase() === NON_ATTRIBUTE_FILTERS.SHORE_HARDNESS.toLowerCase() ||
        f.shortName.toLowerCase() === NON_ATTRIBUTE_FILTERS.POLYMER.toLowerCase() ||
        f.shortName.toLowerCase() === NON_ATTRIBUTE_FILTERS.BRAND.toLowerCase() ||
        f.shortName.toLowerCase() === NON_ATTRIBUTE_FILTERS.MATERIALIDENTIFIER.toLowerCase() ||
        f.shortName.toLowerCase() === NON_ATTRIBUTE_FILTERS.DESIGN.toLowerCase()
      )
    },
    handleApplyFilter(filters) {
      this.filter(filters)
      this.openFilter = false
      setTimeout(() => {
        this.trackSelectedFilterParameters()
      })
    },
    trackSelectedFilterParameters() {
      const activeFilters = this.filters.filter(f => f.isActive)
      const props = activeFilters.length ? this.buildDynamicFilterProps(activeFilters, this.showInchValues, this.$tv) : {}

      const ga4payload = useEventItemConverter().convert({
        event: 'search_results_page_applyfilters',
        add_personalization: true,
        props
      })
      useGtm()?.trackEvent(ga4payload)
    },
    async openBuyBoxSku({sku}) {
      this.product = await this.getProductBySkuNew(sku, this.$globalization.getRegion(), this.$i18n.locale, this.showInchValues, null, PROPERTY_SCOPE_NAMES.PRODUCT_PAGE_NEW)
      if (this.product) {
        this.openCartModal = true
      } else {
        this.$toast.error(this.$tv('productNotAccessible', 'product'))
      }
    },
    async handleCalculate() {
      // This can be triggered by calculate button (experimental mode) or by experimental mode toggle
      await Promise.all([
        this.loadPrices()
      ])
    },
    async loadSuggestionAvailabilities() {
      const suggestions = this.customerSuggestions || []
      if (suggestions.length > 0) {
        const nowUtcIsoString = new Date()
        const suggestionSkus = suggestions.map(s => s?.Sku)
        const suggestionsToRequest = suggestions.map(s => ({
          sku: s?.Sku,
          availabilityDate: nowUtcIsoString,
          quantity: 1,
          defaultPlant: false
        }))
        try {
          const response = await this.postPandaAvailabilitiesBulk(suggestionsToRequest)
          if (response) {
            this.suggestionAvailabilities = response
            this.loadedSuggestionAvailabilitySkus = suggestionSkus
          }
        } catch {
          this.suggestionAvailabilities = []
          this.loadedSuggestionAvailabilitySkus = []
        }
      } else {
        this.suggestionAvailabilities = []
        this.loadedSuggestionAvailabilitySkus = []
      }
    },
    scrollToProducts() {
      const tableElement = document.querySelector('#searchResultsFilterSection')
      if (tableElement) {
        const elementPosition = tableElement.getBoundingClientRect().top
        const offsetPosition = elementPosition + window.pageYOffset - 111
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        })
      }
    },
    resetFilters() {
      this.resetInProgress = true
      this.categoryFilterRef?.resetFilters()
      this.loadProducts().finally(() => {
        this.resetInProgress = false
      })
    }
  },
  watch: {
    deliveryAddressUuid() {
      this.loadProducts()
    },
    PrimaryL2Category(newValue) {
      this.adjustTableColumns(newValue)
      this.adjustFilters(newValue)
      this.componentKey_filter += 1
      this.componentKey += 1
    },
    experimentalMode(newVal, oldVal) {
      // When switching OFF experimental mode, immediately load prices for existing products
      if (oldVal === true && newVal === false && this.products && (this.products.searchResults || this.products.products)) {
        this.handleCalculate()
      }
    }
  }
})
</script>

<style lang="scss">
.search {
  .k-multiselect {
    max-height: 8rem;
    overflow-y: auto;
  }

  &__title {
    margin-bottom: $default-margin-padding-width / 2;

    &__h1 {
      display: inline-block;
      margin: 0;

      &__query {
        word-break: break-all;
      }
    }
  }

  &__results {
    margin: 0 $default-margin-padding-2x $default-margin-padding-2x;
  }

  &__body {
    .suggestion_wrapper {
      display: flex;
      flex-direction: row;

      @include breakpoint-down($lg) {
        flex-direction: column;
      }
    }

    .suggestion_scroll_button {
      margin-top: 4rem;
      padding-right: 1rem;
      display: flex;
      flex-direction: column;
      align-items: flex-start;

      @include breakpoint-down($lg) {
        display: none;
      }

      .scroll-arrow-icon {
        margin-left: 0.5rem;
        width: 1.25rem;
        height: 1.25rem;
      }
    }

    .search-suggestions {
      max-width: 51rem;
      margin: 0 2rem;

      @include breakpoint-down($md) {
        margin: 0;

        .grid {
          margin-left: 1rem;
          margin-right: .5rem;
        }
      }
    }

    @include breakpoint-up($md) {
      background-color: $color__content--background__secondary;
    }

    padding-top: .2rem;

    .button-container {
      display: flex;
      justify-content: space-between;
      flex-flow: row wrap;
      margin: $default-margin-padding-2x .9375rem 0 .9375rem;

      .k-button {
        margin-bottom: $default-margin-padding-width;
      }
    }

    &__filter {
      margin-bottom: 2rem;

      .category-filter-container {
        padding-top: $default-margin-padding-width;
      }
    }

    &__table {
      min-height: 15.625rem;
      padding-bottom: 2rem;
      background-color: $color__content--background__secondary;
    }

    .suggestions-divider {
      display: block;
      width: calc(100% - 4rem);
      height: .125rem;
      margin: 2rem;
      border: none;
      background-color: $color__fst__gray__030;

      @include breakpoint-down($md) {
        width: calc(100% - 2rem);
        margin: 1rem;
      }
    }
  }

  &__no-results {
    h4 {
      margin: 0 2rem;
      padding-bottom: .5rem;

      @include breakpoint-down($md) {
        margin: 0 1rem;
      }
    }

    &__headline {
      height: 2.5rem;
      background-color: $color__fst__primary;
      margin: 0 2rem;

      @include breakpoint-down($md) {
        margin: 0 1rem;
      }
    }

    &__content {
      display: flex;
      justify-content: center;
      background-color: $color__fst__white;
      margin: 0 2rem;

      @include breakpoint-down($md) {
        margin: 0 1rem;
      }
    }

    &__message {
      display: flex;
      align-items: center;
      flex-direction: column;

      .show_no_products_icon {
        padding-top: 1.25rem;
      }

      .icon.icon--svg {
        width: 9.375rem;
        height: 9.375rem;
      }
    }

    &__description {
      padding: 1rem;
      text-align: center;
      font-size: 1.125rem;
    }

    &__button {
      margin-bottom: 1.5rem;
    }
  }
}
</style>
